import { debug } from '@/utils/debug'

/**
 * Microsoft Clarity client API wrapper.
 *
 * Clarity is injected via GTM, so `window.clarity` may not exist yet when the
 * app code runs — or ever, if the tag is blocked by consent/adblock. Calls made
 * before the script loads are buffered and flushed once it appears; if it never
 * appears within the polling window they are dropped silently.
 *
 * All names are typed unions so every event/tag/upgrade reason used across the
 * app is declared in this file — the single source of truth for the Clarity
 * taxonomy (mirrors the GTM `{CATEGORY}_{SUBJECT}_{ACTION}` convention).
 */

declare global {
  interface Window {
    clarity?: (command: string, ...args: unknown[]) => void
  }
}

/**
 * Custom event names. Each becomes its own smart event in the Clarity
 * dashboard (Settings → Smart events), so per-funnel actions that share the
 * same visible button text ("Try again") stay distinguishable.
 */
export const CLARITY_EVENTS = {
  ACTIVATION_ERROR_SHOWN: 'ACTIVATION_ERROR_SHOWN',
  BILLING_UPDATE_ERROR_SHOWN: 'BILLING_UPDATE_ERROR_SHOWN',
  AUTH_RETRY_CLICKED: 'AUTH_RETRY_CLICKED',
  CHECKOUT_RETRY_CLICKED: 'CHECKOUT_RETRY_CLICKED',
  CHECKOUT_PAYMENT_LINK_RETRY_CLICKED: 'CHECKOUT_PAYMENT_LINK_RETRY_CLICKED',
  CONTENT_DETAIL_RETRY_CLICKED: 'CONTENT_DETAIL_RETRY_CLICKED',
  OFFER_RETRY_CLICKED: 'OFFER_RETRY_CLICKED',
  PLAYER_RETRY_CLICKED: 'PLAYER_RETRY_CLICKED',
} as const

export type ClarityEvent = typeof CLARITY_EVENTS[keyof typeof CLARITY_EVENTS]

/** Session tag keys — each becomes a filter in the Clarity dashboard. */
export const CLARITY_TAGS = {
  APP_CODE: 'appCode',
  SCENE: 'scene',
  AB_VARIANT: 'abVariant',
  BRANDING_TAG: 'brandingTag',
  ACTIVATION_ERROR_REASON: 'activationErrorReason',
  BILLING_ERROR_CATEGORY: 'billingErrorCategory',
  AUTH_ERROR_CODE: 'authErrorCode',
  CHECKOUT_ERROR_CODE: 'checkoutErrorCode',
  PLAYER_ERROR_CODE: 'playerErrorCode',
} as const

export type ClarityTag = typeof CLARITY_TAGS[keyof typeof CLARITY_TAGS]

/**
 * Reasons to prioritize a session recording. Clarity samples recordings past
 * its daily cap; upgraded sessions are kept preferentially.
 */
export const CLARITY_UPGRADE_REASONS = {
  BILLING_UPDATE_ERROR: 'billing_update_error',
  CHECKOUT_ERROR: 'checkout_error',
  PLAYER_ERROR: 'player_error',
} as const

export type ClarityUpgradeReason = typeof CLARITY_UPGRADE_REASONS[keyof typeof CLARITY_UPGRADE_REASONS]

type QueuedCall =
  | ['event', ClarityEvent]
  | ['set', ClarityTag, string | string[]]
  | ['upgrade', ClarityUpgradeReason]

const MAX_QUEUE_SIZE = 64
const FLUSH_POLL_INTERVAL_MS = 500
const FLUSH_POLL_TIMEOUT_MS = 30_000

let queue: QueuedCall[] = []
let pollTimer: ReturnType<typeof setInterval> | null = null
let pollDeadline = 0
const sentTags = new Map<ClarityTag, string>()

const getClarityFn = (): Window['clarity'] | null => {
  if (typeof window === 'undefined') return null
  return typeof window.clarity === 'function' ? window.clarity : null
}

const send = (clarityFn: NonNullable<Window['clarity']>, call: QueuedCall) => {
  const [command, ...args] = call
  try {
    clarityFn(command, ...args)
    debug.log('🔍 Clarity:', command, ...args)
  } catch (e) {
    debug.warn('🔍 Clarity call failed:', e)
  }
}

const stopPolling = () => {
  if (pollTimer !== null) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

const flushQueue = (clarityFn: NonNullable<Window['clarity']>) => {
  const pending = queue
  queue = []
  stopPolling()
  pending.forEach((call) => send(clarityFn, call))
}

const startPolling = () => {
  pollDeadline = Date.now() + FLUSH_POLL_TIMEOUT_MS
  if (pollTimer !== null) return

  pollTimer = setInterval(() => {
    const clarityFn = getClarityFn()
    if (clarityFn) {
      flushQueue(clarityFn)
    } else if (Date.now() >= pollDeadline) {
      // Clarity never loaded (blocked or absent) — drop buffered calls.
      queue = []
      stopPolling()
    }
  }, FLUSH_POLL_INTERVAL_MS)
}

const dispatch = (call: QueuedCall) => {
  if (typeof window === 'undefined') return

  const clarityFn = getClarityFn()
  if (clarityFn) {
    if (queue.length > 0) flushQueue(clarityFn)
    send(clarityFn, call)
    return
  }

  if (queue.length >= MAX_QUEUE_SIZE) queue.shift()
  queue.push(call)
  startPolling()
}

export const clarity = {
  /** Track a user action as a Clarity custom (API) event. */
  event: (name: ClarityEvent) => {
    dispatch(['event', name])
  },

  /**
   * Tag the current session (becomes a dashboard filter). Repeat calls with
   * the same key + value are deduped; a new value for the same key is sent.
   */
  set: (key: ClarityTag, value: string | string[]) => {
    const serialized = Array.isArray(value) ? value.join('|') : value
    if (!serialized || sentTags.get(key) === serialized) return
    sentTags.set(key, serialized)
    dispatch(['set', key, value])
  },

  /** Prioritize this session's recording against Clarity's daily sampling. */
  upgrade: (reason: ClarityUpgradeReason) => {
    dispatch(['upgrade', reason])
  },
}
